import React from 'react'
import { Divider } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses, DialogProps } from '@paintscout/ui'
import { createStyles, withStyles } from '@material-ui/core/styles'
import type { OptionsDocument } from 'paintscout'
import {
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputField,
  FormControlLabel,
  ActiveSwitch
} from '@paintscout/ui'
import clone from 'lodash/clone'
import type { QuoteTemplateOption } from '@paintscout/util/builder'
import { getObjectLabels, updateQuoteTemplateOption, getFeature } from '@paintscout/util/builder'
import CloseIcon from '@material-ui/icons/Close'
import QuoteTypeSelect from '../../QuoteTypeSelect'
import { useFlags } from 'shared/components/common/FlagsProvider'

export interface EditQuoteTemplateOptionDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  item: QuoteTemplateOption
  options: OptionsDocument
  onConfirm?: (options: OptionsDocument, type: string) => void
  onCancel?: () => void
  showActiveToggle?: boolean
}

export interface EditQuoteTemplateOptionDialogState {
  item: QuoteTemplateOption
}

const styles = (theme: Theme) => {
  return createStyles({
    root: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gridGap: theme.spacing(2)
    },
    divider: {},
    dialogTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    switchLabel: {
      marginLeft: '0 !important',
      marginRight: 0
    },
    dialogContent: {
      maxHeight: '70vh',
      overflowY: 'auto',
      [theme.breakpoints.down('sm')]: {
        maxHeight: '75vh'
      }
    }
  })
}

class EditQuoteTemplateOptionDialog extends React.PureComponent<
  EditQuoteTemplateOptionDialogProps,
  EditQuoteTemplateOptionDialogState
> {
  private signatureData?: string

  constructor(props: EditQuoteTemplateOptionDialogProps) {
    super(props)

    const { item } = this.props

    this.state = {
      item: clone(item)
    }
  }

  public render() {
    const { loading, classes, options, onConfirm: _onConfirm, onCancel, showActiveToggle, ...otherProps } = this.props
    const { item } = this.state
    const objectLabels = getObjectLabels({ options })
    const toggleValue = typeof item.active !== 'undefined' ? !!item.active : true

    return (
      <Dialog fullWidth={true} maxWidth={'md'} {...otherProps}>
        <DialogTitle
          loading={loading}
          classes={{ root: classes.dialogTitle }}
          rightContent={
            <IconButton size="small" onClick={onCancel} disabled={loading}>
              <CloseIcon />
            </IconButton>
          }
        >
          Edit Template
        </DialogTitle>
        <DialogContent classes={{ root: classes.dialogContent }}>
          <form
            className={classes.root}
            id="edit-template-option-form"
            onSubmit={(e) => {
              e.preventDefault()
              this.handleConfirm()
            }}
          >
            <InputField
              data-testid="template-label"
              label={'Label'}
              name={'label'}
              required
              value={item.label}
              fullWidth={true}
              onChange={this.handleChange}
            />
            <Divider classes={{ root: classes.divider }} />
            <QuoteTypeSelect
              label={`${objectLabels.quote.value} Types`}
              variant={getFeature({ options, path: 'quotes.lineItemMediaOptions.enabled' }) ? 'multi' : 'single'}
              onChange={this.handleQuoteTypeChange}
              value={item.quoteTypes}
            />
          </form>
        </DialogContent>
        <DialogActions
          showBorder={true}
          leftButton={
            showActiveToggle ? (
              <FormControlLabel
                className={classes.switchLabel}
                control={<ActiveSwitch color="primary" checked={toggleValue} onChange={this.handleToggle} />}
                label="Active"
                labelPlacement="end"
              />
            ) : undefined
          }
        >
          <Button disabled={loading} variant={'contained'} type="submit" form="edit-template-option-form">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  public handleChange = (event: any) => {
    const { name, value } = event.target

    this.setState({
      item: {
        ...this.state.item,
        [name]: value
      }
    })
  }

  public handleCheckboxChange = (event: any, value: string) => {
    const { name } = event.target

    this.setState({
      item: {
        ...this.state.item,
        [name]: value
      }
    })
  }

  public handleConfirm = () => {
    if (this.props.onConfirm) {
      const { options } = this.props
      const updatedOptions = updateQuoteTemplateOption({ options, quoteTemplateOption: this.state.item })
      this.props.onConfirm(updatedOptions, this.state.item.quoteTypes[0])
    }
  }

  public handleQuoteTypeChange = (values: string[]) => {
    this.setState({
      item: {
        ...this.state.item,
        quoteTypes: values
      }
    })
  }

  public handleToggle = (_event: React.ChangeEvent<HTMLElement>, _checked: boolean) => {
    this.setState({
      item: {
        ...this.state.item,
        active: !this.state.item.active
      }
    })
  }
}

const StyledEditQuoteTemplateOptionDialog = withStyles(styles)(EditQuoteTemplateOptionDialog)

function EditQuoteTemplateOptionDialogWithFlags(props: Omit<EditQuoteTemplateOptionDialogProps, 'showActiveToggle'>) {
  const { canDeactivateSettingsItems } = useFlags()
  return <StyledEditQuoteTemplateOptionDialog {...props} showActiveToggle={!!canDeactivateSettingsItems} />
}

export default EditQuoteTemplateOptionDialogWithFlags
